import {FC, useEffect, useMemo, useState} from 'react'
import moment from 'moment'
import Chart from 'react-apexcharts'
import {PageTitle} from '../../../../_metronic/layout/core'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {Button} from '../../../components/Button/Button'
import {Datatable} from '../../../components/Datatable/Datatable'
import {Input} from '../../../components/Input/Input'
import {Loading} from '../../../components/Loading/Loading'
import {GET, POST} from '../../../services/api'
import useAuth from '../../../hooks/useAuth'
import {CardInfoGuide, CardInfoContent} from '../../../components/CardInfoGuide/CardInfoGuide'
import {
  salesReportInfo,
  earnedReportInfo,
  redeemedReportInfo,
  leftoverReportInfo,
  leftoverReportInfoFood,
  reversalReportInfo,
  ordersWithCashbackInfo,
  avgTicketInfo,
  customersBalanceInfo,
  ruleInfoByScope,
  trendReportInfo,
  topCustomersInfo,
  ticketCompareInfo,
  checkoutShareInfo,
  salesPerRedeemedInfo,
  avgRedeemTimeInfo,
} from './components/reportCardInfoContent'

const MetricInfo: FC<{content?: CardInfoContent}> = ({content}) => {
  if (!content) return null
  return <CardInfoGuide content={content} interaction='both' />
}

type CashbackTransactionType = 'earn' | 'redeem' | 'reversal'
type TimeGrouping = 'day' | 'month' | 'year'
type ChartView = 'volume' | 'redeemers'
type RuleScope = 'variation' | 'product' | 'category' | 'catalog'

type CashbackConfigData = {
  baseCashbackEnabled: boolean
  baseCashbackRate: number
  onlySpecificRules: boolean
  maxEarningPerOrder: number
  minOrderValueToEarn: number
}

type CashbackTransaction = {
  id: number
  customerId?: number
  orderId: number | null
  type: CashbackTransactionType
  amount: number
  description: string | null
  customerCashbackId: number | null
  created: string
  customerCashback?: {
    id?: number
    customerId?: number
    balance?: number
    customer?: {
      id?: number
      name?: string
      email?: string
    }
  }
}

type OrderItem = {
  id: number
  totalReais: number
  created: string
}

type ReportStats = {
  sales: number
  ordersCount: number
  ordersWithCashback: number
  earned: number
  redeemed: number
  reversed: number
  potential: number
  leftover: number
  generatedAbove: number
  canEstimatePotential: boolean
  redemptionRate: number
  avgTicket: number
  avgCashbackPerOrder: number
  customersCount: number
  walletBalance: number
  earnCount: number
  redeemCount: number
  reversalCount: number
  baseCashbackRate: number
  ticketWithRedeem: number
  ticketWithoutRedeem: number
  ordersWithRedeem: number
  ordersWithoutRedeem: number
  ticketDeltaPercent: number | null
  checkoutShare: number
  salesWithRedeem: number
  salesPerRedeemedReal: number | null
  avgRedeemDays: number | null
  redeemPairsCount: number
}

type TrendPoint = {
  date: string
  earned: number
  redeemed: number
  reversed: number
  sales: number
  firstRedeemers: number
  repeatRedeemers: number
}

type CustomerRow = {
  customerId: number
  customerName: string
  email: string
  earned: number
  redeemed: number
  earnedDisplay: string
  redeemedDisplay: string
  orders: number
}

type RuleCount = {
  scope: RuleScope
  label: string
  count: number
}

const FOOD_SCOPES: RuleScope[] = ['product', 'category']
const DEFAULT_SCOPES: RuleScope[] = ['variation', 'product', 'category', 'catalog']

const SCOPE_LABELS: Record<RuleScope, string> = {
  variation: 'Variação',
  product: 'Produto',
  category: 'Categoria',
  catalog: 'Catálogo',
}

const getScopesByPlugin = (isFood: boolean): RuleScope[] => (isFood ? FOOD_SCOPES : DEFAULT_SCOPES)

const getRulesHint = (isFood: boolean) =>
  isFood
    ? 'Regras específicas de produto ou categoria não entram nesse cálculo.'
    : 'Regras específicas de produto, categoria, catálogo ou variação não entram nesse cálculo.'

const formatMoney = (value?: number | null) => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) return 'R$ 0,00'
  return new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'}).format(Number(value))
}

const roundMoney = (value: number) => Math.round(value * 100) / 100

const absAmount = (value: number) => Math.abs(Number(value) || 0)

const formatPercent = (value?: number | null) => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) return 'N/A'
  return `${Number(value).toLocaleString('pt-BR', {maximumFractionDigits: 1})}%`
}

const formatDays = (value?: number | null) => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) return 'N/A'
  const rounded = Math.round(Number(value) * 10) / 10
  if (rounded < 1) return 'Menos de 1 dia'
  return `${rounded.toLocaleString('pt-BR')} dia${rounded === 1 ? '' : 's'}`
}

const getOrderTotalReais = (order: any) => {
  const raw = Number(order?.totalWithoutDelivery ?? order?.total ?? 0)
  return (Number(raw) || 0) / 100
}

const getTransactionCustomerId = (item: CashbackTransaction) =>
  Number(
    item.customerId ||
      item.customerCashback?.customerId ||
      item.customerCashback?.customer?.id ||
      0
  )

const getTransactionCustomerName = (item: CashbackTransaction) =>
  item.customerCashback?.customer?.name || `Cliente #${getTransactionCustomerId(item)}`

const getTransactionCustomerEmail = (item: CashbackTransaction) =>
  item.customerCashback?.customer?.email || '-'

const getTrendKey = (dateValue: string, grouping: TimeGrouping) => {
  const parsed = moment(dateValue)
  if (!parsed.isValid()) return ''
  if (grouping === 'month') return parsed.format('YYYY-MM')
  if (grouping === 'year') return parsed.format('YYYY')
  return parsed.format('YYYY-MM-DD')
}

const formatTrendLabel = (key: string, grouping: TimeGrouping) => {
  if (grouping === 'day') return moment(key).format('DD/MM/YYYY')
  if (grouping === 'month') return moment(`${key}-01`).format('MMM YYYY')
  return key
}

const normalizeConfig = (payload: any): CashbackConfigData => ({
  baseCashbackEnabled:
    payload?.baseCashbackEnabled !== undefined ? !!payload.baseCashbackEnabled : true,
  baseCashbackRate: Number(payload?.baseCashbackRate) || 0,
  onlySpecificRules: !!payload.onlySpecificRules,
  maxEarningPerOrder: Number(payload?.maxEarningPerOrder) || 0,
  minOrderValueToEarn: Number(payload?.minOrderValueToEarn) || 0,
})

const extractList = (response: any): any[] => {
  if (Array.isArray(response?.data)) return response.data
  if (Array.isArray(response)) return response
  return []
}

const buildStats = (
  transactions: CashbackTransaction[],
  orders: OrderItem[],
  config: CashbackConfigData
): ReportStats => {
  let earned = 0
  let redeemed = 0
  let reversed = 0
  let earnCount = 0
  let redeemCount = 0
  let reversalCount = 0

  const orderIdsWithCashback = new Set<number>()
  const customers = new Set<number>()
  const wallets = new Map<number, number>()

  transactions.forEach((item) => {
    const amount = absAmount(item.amount)
    const customerId = getTransactionCustomerId(item)
    if (customerId) customers.add(customerId)
    if (item.orderId) orderIdsWithCashback.add(item.orderId)
    if (item.customerCashbackId) {
      wallets.set(item.customerCashbackId, Number(item.customerCashback?.balance || 0))
    }

    if (item.type === 'earn') {
      earned += amount
      earnCount += 1
    }
    if (item.type === 'redeem') {
      redeemed += amount
      redeemCount += 1
    }
    if (item.type === 'reversal') {
      reversed += amount
      reversalCount += 1
    }
  })

  const sales = orders.reduce((total, order) => total + order.totalReais, 0)
  const canEstimatePotential =
    config.baseCashbackEnabled && !config.onlySpecificRules && config.baseCashbackRate > 0

  let potential = 0
  if (canEstimatePotential) {
    potential = orders.reduce((total, order) => {
      if (config.minOrderValueToEarn > 0 && order.totalReais < config.minOrderValueToEarn) {
        return total
      }
      let estimated = (order.totalReais * config.baseCashbackRate) / 100
      if (config.maxEarningPerOrder > 0) {
        estimated = Math.min(estimated, config.maxEarningPerOrder)
      }
      return total + estimated
    }, 0)
  }

  const leftover = canEstimatePotential ? Math.max(0, potential - earned) : 0
  const generatedAbove = canEstimatePotential ? Math.max(0, earned - potential) : 0

  const redeemOrderIds = new Set<number>()
  transactions.forEach((item) => {
    if (item.type === 'redeem' && item.orderId) redeemOrderIds.add(item.orderId)
  })

  let salesWithRedeem = 0
  let salesWithoutRedeem = 0
  let ordersWithRedeem = 0
  let ordersWithoutRedeem = 0
  orders.forEach((order) => {
    if (redeemOrderIds.has(order.id)) {
      salesWithRedeem += order.totalReais
      ordersWithRedeem += 1
    } else {
      salesWithoutRedeem += order.totalReais
      ordersWithoutRedeem += 1
    }
  })

  const ticketWithRedeem = ordersWithRedeem > 0 ? salesWithRedeem / ordersWithRedeem : 0
  const ticketWithoutRedeem = ordersWithoutRedeem > 0 ? salesWithoutRedeem / ordersWithoutRedeem : 0
  const ticketDeltaPercent =
    ordersWithRedeem > 0 && ordersWithoutRedeem > 0 && ticketWithoutRedeem > 0
      ? roundMoney(((ticketWithRedeem - ticketWithoutRedeem) / ticketWithoutRedeem) * 100)
      : null

  const earnsByCustomer = new Map<number, CashbackTransaction[]>()
  transactions.forEach((item) => {
    if (item.type !== 'earn') return
    const customerId = getTransactionCustomerId(item)
    if (!customerId) return
    const list = earnsByCustomer.get(customerId) || []
    list.push(item)
    earnsByCustomer.set(customerId, list)
  })
  earnsByCustomer.forEach((list) => {
    list.sort((a, b) => moment(a.created).valueOf() - moment(b.created).valueOf())
  })

  const redeemDays: number[] = []
  const usedEarnIds = new Set<number>()
  transactions
    .filter((item) => item.type === 'redeem')
    .sort((a, b) => moment(a.created).valueOf() - moment(b.created).valueOf())
    .forEach((redeem) => {
      const customerId = getTransactionCustomerId(redeem)
      if (!customerId) return
      const earns = earnsByCustomer.get(customerId) || []
      const matched = earns.find(
        (earn) =>
          !usedEarnIds.has(earn.id) &&
          moment(earn.created).isSameOrBefore(redeem.created) &&
          earn.orderId !== redeem.orderId
      )
      if (!matched) return
      usedEarnIds.add(matched.id)
      const days = moment(redeem.created).diff(moment(matched.created), 'days', true)
      if (days >= 0) redeemDays.push(days)
    })

  const avgRedeemDays =
    redeemDays.length > 0
      ? roundMoney(redeemDays.reduce((total, days) => total + days, 0) / redeemDays.length)
      : null

  return {
    sales: roundMoney(sales),
    ordersCount: orders.length,
    ordersWithCashback: orderIdsWithCashback.size,
    earned: roundMoney(earned),
    redeemed: roundMoney(redeemed),
    reversed: roundMoney(reversed),
    potential: roundMoney(potential),
    leftover: roundMoney(leftover),
    generatedAbove: roundMoney(generatedAbove),
    canEstimatePotential,
    redemptionRate: earned > 0 ? roundMoney((redeemed / earned) * 100) : 0,
    avgTicket: orders.length > 0 ? roundMoney(sales / orders.length) : 0,
    avgCashbackPerOrder:
      orderIdsWithCashback.size > 0 ? roundMoney(earned / orderIdsWithCashback.size) : 0,
    customersCount: customers.size,
    walletBalance: roundMoney(
      Array.from(wallets.values()).reduce((total, balance) => total + absAmount(balance), 0)
    ),
    earnCount,
    redeemCount,
    reversalCount,
    baseCashbackRate: config.baseCashbackRate,
    ticketWithRedeem: roundMoney(ticketWithRedeem),
    ticketWithoutRedeem: roundMoney(ticketWithoutRedeem),
    ordersWithRedeem,
    ordersWithoutRedeem,
    ticketDeltaPercent,
    checkoutShare: sales > 0 ? roundMoney((redeemed / sales) * 100) : 0,
    salesWithRedeem: roundMoney(salesWithRedeem),
    salesPerRedeemedReal: redeemed > 0 ? roundMoney(salesWithRedeem / redeemed) : null,
    avgRedeemDays,
    redeemPairsCount: redeemDays.length,
  }
}

const buildTrendData = (
  transactions: CashbackTransaction[],
  orders: OrderItem[],
  grouping: TimeGrouping
): TrendPoint[] => {
  const grouped: Record<string, TrendPoint> = {}

  const ensureKey = (key: string) => {
    if (!key) return
    if (!grouped[key]) {
      grouped[key] = {
        date: key,
        earned: 0,
        redeemed: 0,
        reversed: 0,
        sales: 0,
        firstRedeemers: 0,
        repeatRedeemers: 0,
      }
    }
  }

  transactions.forEach((item) => {
    const key = getTrendKey(item.created, grouping)
    ensureKey(key)
    if (!key) return
    const amount = absAmount(item.amount)
    if (item.type === 'earn') grouped[key].earned += amount
    if (item.type === 'redeem') grouped[key].redeemed += amount
    if (item.type === 'reversal') grouped[key].reversed += amount
  })

  orders.forEach((order) => {
    const key = getTrendKey(order.created, grouping)
    ensureKey(key)
    if (!key) return
    grouped[key].sales += order.totalReais
  })

  const firstRedeemKeyByCustomer = new Map<number, string>()
  const firstRedeemersByKey: Record<string, Set<number>> = {}
  const repeatRedeemersByKey: Record<string, Set<number>> = {}

  transactions
    .filter((item) => item.type === 'redeem')
    .sort((a, b) => moment(a.created).valueOf() - moment(b.created).valueOf())
    .forEach((item) => {
      const customerId = getTransactionCustomerId(item)
      const key = getTrendKey(item.created, grouping)
      if (!customerId || !key) return
      ensureKey(key)
      if (!firstRedeemersByKey[key]) firstRedeemersByKey[key] = new Set()
      if (!repeatRedeemersByKey[key]) repeatRedeemersByKey[key] = new Set()

      if (!firstRedeemKeyByCustomer.has(customerId)) {
        firstRedeemKeyByCustomer.set(customerId, key)
        firstRedeemersByKey[key].add(customerId)
        return
      }
      if (firstRedeemKeyByCustomer.get(customerId) !== key) {
        repeatRedeemersByKey[key].add(customerId)
      }
    })

  Object.keys(grouped).forEach((key) => {
    grouped[key].firstRedeemers = firstRedeemersByKey[key]?.size || 0
    grouped[key].repeatRedeemers = repeatRedeemersByKey[key]?.size || 0
  })

  return Object.values(grouped)
    .map((item) => ({
      ...item,
      earned: roundMoney(item.earned),
      redeemed: roundMoney(item.redeemed),
      reversed: roundMoney(item.reversed),
      sales: roundMoney(item.sales),
    }))
    .sort((a, b) => a.date.localeCompare(b.date))
}

const buildCustomerRows = (transactions: CashbackTransaction[]): CustomerRow[] => {
  const grouped = new Map<
    number,
    {customerName: string; email: string; earned: number; redeemed: number; orders: Set<number>}
  >()

  transactions.forEach((item) => {
    const customerId = getTransactionCustomerId(item)
    if (!customerId) return
    const current = grouped.get(customerId) || {
      customerName: getTransactionCustomerName(item),
      email: getTransactionCustomerEmail(item),
      earned: 0,
      redeemed: 0,
      orders: new Set<number>(),
    }

    if (item.type === 'earn') current.earned += absAmount(item.amount)
    if (item.type === 'redeem') current.redeemed += absAmount(item.amount)
    if (item.orderId) current.orders.add(item.orderId)
    grouped.set(customerId, current)
  })

  return Array.from(grouped.entries())
    .map(([customerId, item]) => ({
      customerId,
      customerName: item.customerName,
      email: item.email,
      earned: roundMoney(item.earned),
      redeemed: roundMoney(item.redeemed),
      earnedDisplay: formatMoney(item.earned),
      redeemedDisplay: formatMoney(item.redeemed),
      orders: item.orders.size,
    }))
    .sort((a, b) => b.earned - a.earned)
    .slice(0, 20)
}

const fetchAllOrders = async (startDate: string, endDate: string): Promise<OrderItem[]> => {
  const take = 200
  let page = 1
  let pageCount = 1
  const orders: OrderItem[] = []

  while (page <= pageCount && page <= 10) {
    const response = await POST(`/orders/filter-admin_v2?page=${page}&take=${take}`, {
      startDate: `${startDate}T00:00:00`,
      finalDate: `${endDate}T23:59:59`,
    })
    const list = extractList(response)
    orders.push(
      ...list.map((order: any) => ({
        id: Number(order?.id || 0),
        totalReais: getOrderTotalReais(order),
        created: order?.created || '',
      }))
    )
    pageCount = Number(response?.meta?.pageCount || 1)
    page += 1
  }

  return orders
}

const fetchRuleCounts = async (isFood: boolean): Promise<RuleCount[]> => {
  const scopes = getScopesByPlugin(isFood)
  const responses = await Promise.all(
    scopes.map((scope) => GET(`/cashback-management?typeCashback=${scope}`))
  )

  return scopes.map((scope, index) => ({
    scope,
    label: SCOPE_LABELS[scope],
    count: extractList(responses[index]).filter((item) => item?.status !== false).length,
  }))
}

const CashbackReportContent: FC = () => {
  const {loginInfo} = useAuth()
  const isFood = loginInfo?.plugintype === 'food'
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [dateStart, setDateStart] = useState(moment().startOf('month').format('YYYY-MM-DD'))
  const [dateEnd, setDateEnd] = useState(moment().format('YYYY-MM-DD'))
  const [timeGrouping, setTimeGrouping] = useState<TimeGrouping>('day')
  const [chartView, setChartView] = useState<ChartView>('volume')
  const [transactions, setTransactions] = useState<CashbackTransaction[]>([])
  const [orders, setOrders] = useState<OrderItem[]>([])
  const [config, setConfig] = useState<CashbackConfigData>(normalizeConfig({}))
  const [ruleCounts, setRuleCounts] = useState<RuleCount[]>([])

  const loadReport = async (start = dateStart, end = dateEnd) => {
    setLoading(true)
    setError('')
    try {
      const [configRes, transactionsRes, ordersList, rulesList] = await Promise.all([
        GET('/cashback-config'),
        POST('/cashback/transaction/filter', {
          createdFrom: moment(start).startOf('day').toISOString(),
          createdTo: moment(end).endOf('day').toISOString(),
        }),
        fetchAllOrders(start, end),
        fetchRuleCounts(isFood),
      ])

      const currentConfig = Array.isArray(configRes?.data)
        ? configRes.data[0]
        : configRes?.data?.data?.[0] ?? configRes?.data

      setConfig(normalizeConfig(currentConfig))
      setTransactions(extractList(transactionsRes) as CashbackTransaction[])
      setOrders(ordersList)
      setRuleCounts(rulesList)
    } catch (err) {
      console.error(err)
      setTransactions([])
      setOrders([])
      setRuleCounts([])
      setError('Não foi possível carregar o relatório de cashback.')
    } finally {
      setLoading(false)
    }
  }

  const handleClearFilters = () => {
    const start = moment().startOf('month').format('YYYY-MM-DD')
    const end = moment().format('YYYY-MM-DD')
    setDateStart(start)
    setDateEnd(end)
    loadReport(start, end)
  }

  useEffect(() => {
    loadReport()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const stats = useMemo(
    () => buildStats(transactions, orders, config),
    [transactions, orders, config]
  )
  const trendData = useMemo(
    () => buildTrendData(transactions, orders, timeGrouping),
    [transactions, orders, timeGrouping]
  )
  const customerRows = useMemo(() => buildCustomerRows(transactions), [transactions])

  if (loading) return <Loading />

  return (
    <div className='row g-5 gx-xxl-12'>
      <div className='card card-xxl-stretch mb-5 mb-xl-12'>
        <div className='card-header py-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Relatório de Cashback</span>
            <BreadCrumbs />
            <span className='text-muted mt-1 fw-bold fs-7'>
              Vendas, cashback gerado, resgatado e o que a loja deixou de gerar no período.
            </span>
          </h3>
        </div>

        <div className='card-body border-bottom'>
          {error && (
            <div className='alert alert-danger d-flex align-items-center p-5 mb-10'>
              <i className='fas fa-exclamation-triangle fs-2hx text-danger me-4'></i>
              <div className='d-flex flex-column'>
                <h4 className='mb-1 text-danger'>Erro ao carregar dados</h4>
                <span>{error}</span>
              </div>
            </div>
          )}

          <div className='row g-3 mt-2'>
            <div className='col-md-4 mt-0'>
              <Input
                label='Data inicial'
                inputAttr={{type: 'date'}}
                value={dateStart}
                change={setDateStart}
                maxLength={dateEnd}
                marginTop='0'
              />
            </div>
            <div className='col-md-4 mt-0'>
              <Input
                label='Data final'
                inputAttr={{type: 'date'}}
                value={dateEnd}
                change={setDateEnd}
                minLength={dateStart}
                marginTop='0'
              />
            </div>
            <div className='col-md-4 d-flex align-items-end justify-content-md-end gap-2 mt-0'>
              <Button
                text='Limpar'
                color='danger'
                iconColor='#FFF'
                size='small'
                horizontalPadding={4}
                btnAttr={{type: 'button'}}
                click={handleClearFilters}
                btnClass='mb-0'
              />
              <Button
                text='Filtrar'
                color='primary'
                icon='fas fa-filter'
                iconColor='#FFF'
                size='small'
                horizontalPadding={6}
                btnAttr={{type: 'button'}}
                click={() => loadReport()}
                btnClass='mb-0 fw-bolder'
              />
            </div>
          </div>
        </div>

        <div className='card-body bg-light'>
          <div className='row g-5 mb-8'>
            <div className='col-xl-3 col-md-6'>
              <div className='card bg-body shadow-sm border-0 h-100'>
                <div className='card-body d-flex flex-column justify-content-center'>
                  <div className='d-flex align-items-center justify-content-between mb-4'>
                    <span className='text-gray-600 fw-bold fs-6'>Vendas no período</span>
                    <div className='d-flex align-items-center gap-3'>
                      <MetricInfo content={salesReportInfo} />
                      <i className='fas fa-shopping-bag fs-2x text-primary'></i>
                    </div>
                  </div>
                  <span className='text-dark fw-bolder fs-2'>{formatMoney(stats.sales)}</span>
                  <span className='text-muted fw-semibold fs-7 mt-1'>
                    {stats.ordersCount} pedidos
                  </span>
                </div>
              </div>
            </div>
            <div className='col-xl-3 col-md-6'>
              <div className='card bg-body shadow-sm border-0 h-100'>
                <div className='card-body d-flex flex-column justify-content-center'>
                  <div className='d-flex align-items-center justify-content-between mb-4'>
                    <span className='text-gray-600 fw-bold fs-6'>Cashback gerado</span>
                    <div className='d-flex align-items-center gap-3'>
                      <MetricInfo content={earnedReportInfo} />
                      <i className='fas fa-coins fs-2x text-success'></i>
                    </div>
                  </div>
                  <span className='text-dark fw-bolder fs-2'>{formatMoney(stats.earned)}</span>
                  <span className='text-muted fw-semibold fs-7 mt-1'>
                    {stats.earnCount} acúmulos
                  </span>
                </div>
              </div>
            </div>
            <div className='col-xl-3 col-md-6'>
              <div className='card bg-body shadow-sm border-0 h-100'>
                <div className='card-body d-flex flex-column justify-content-center'>
                  <div className='d-flex align-items-center justify-content-between mb-4'>
                    <span className='text-gray-600 fw-bold fs-6'>Cashback resgatado</span>
                    <div className='d-flex align-items-center gap-3'>
                      <MetricInfo content={redeemedReportInfo} />
                      <i className='fas fa-wallet fs-2x text-warning'></i>
                    </div>
                  </div>
                  <span className='text-dark fw-bolder fs-2'>{formatMoney(stats.redeemed)}</span>
                  <span className='text-muted fw-semibold fs-7 mt-1'>
                    {stats.redemptionRate}% do gerado
                  </span>
                </div>
              </div>
            </div>
            <div className='col-xl-3 col-md-6'>
              <div className='card bg-light-danger border-0 h-100'>
                <div className='card-body d-flex flex-column justify-content-center'>
                  <div className='d-flex align-items-center justify-content-between mb-4'>
                    <span className='text-danger fw-bold fs-6'>Deixado de gerar</span>
                    <div className='d-flex align-items-center gap-3'>
                      <MetricInfo
                        content={isFood ? leftoverReportInfoFood : leftoverReportInfo}
                      />
                      <i className='fas fa-chart-line fs-2x text-danger opacity-50'></i>
                    </div>
                  </div>
                  <span className='text-dark fw-bolder fs-2'>
                    {stats.canEstimatePotential ? formatMoney(stats.leftover) : 'N/A'}
                  </span>
                  <span className='text-muted fw-semibold fs-7 mt-1'>
                    {stats.canEstimatePotential
                      ? `Potencial ${formatMoney(stats.potential)}${
                          stats.generatedAbove > 0 ? ' · gerou acima da estimativa' : ''
                        }`
                      : 'Sem taxa base para estimar'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className='row g-5 mb-8'>
            <div className='col-xl-3 col-md-6'>
              <div className='bg-light-info rounded d-flex flex-column p-6 h-100'>
                <div className='d-flex align-items-center justify-content-between'>
                  <span className='text-muted fw-semibold fs-7'>Estornos</span>
                  <MetricInfo content={reversalReportInfo} />
                </div>
                <span className='text-info fs-2 fw-bold'>{formatMoney(stats.reversed)}</span>
                <span className='text-muted fs-8'>{stats.reversalCount} movimentações</span>
              </div>
            </div>
            <div className='col-xl-3 col-md-6'>
              <div className='bg-light-primary rounded d-flex flex-column p-6 h-100'>
                <div className='d-flex align-items-center justify-content-between'>
                  <span className='text-muted fw-semibold fs-7'>Pedidos com cashback</span>
                  <MetricInfo content={ordersWithCashbackInfo} />
                </div>
                <span className='text-primary fs-2 fw-bold'>{stats.ordersWithCashback}</span>
                <span className='text-muted fs-8'>de {stats.ordersCount} vendas</span>
              </div>
            </div>
            <div className='col-xl-3 col-md-6'>
              <div className='bg-light-success rounded d-flex flex-column p-6 h-100'>
                <div className='d-flex align-items-center justify-content-between'>
                  <span className='text-muted fw-semibold fs-7'>Ticket médio</span>
                  <MetricInfo content={avgTicketInfo} />
                </div>
                <span className='text-success fs-2 fw-bold'>{formatMoney(stats.avgTicket)}</span>
                <span className='text-muted fs-8'>
                  Média de cashback {formatMoney(stats.avgCashbackPerOrder)}
                </span>
              </div>
            </div>
            <div className='col-xl-3 col-md-6'>
              <div className='bg-light-warning rounded d-flex flex-column p-6 h-100'>
                <div className='d-flex align-items-center justify-content-between'>
                  <span className='text-muted fw-semibold fs-7'>Clientes e saldo</span>
                  <MetricInfo content={customersBalanceInfo} />
                </div>
                <span className='text-warning fs-2 fw-bold'>{stats.customersCount}</span>
                <span className='text-muted fs-8'>
                  Saldo atual {formatMoney(stats.walletBalance)}
                </span>
              </div>
            </div>
          </div>

          <div className='row g-5 mb-8'>
            {ruleCounts.map((rule) => (
              <div key={rule.scope} className={isFood ? 'col-xl-6 col-md-6' : 'col-xl-3 col-md-6'}>
                <div className='card bg-body shadow-sm border-0 h-100'>
                  <div className='card-body py-6'>
                    <div className='d-flex align-items-center justify-content-between'>
                      <span className='text-muted fw-semibold fs-7'>Regras de {rule.label}</span>
                      <MetricInfo content={ruleInfoByScope[rule.scope]} />
                    </div>
                    <div className='d-flex align-items-end justify-content-between mt-2'>
                      <span className='text-dark fw-bolder fs-2'>{rule.count}</span>
                      <span className='text-muted fs-8'>ativas no momento</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className='alert alert-info d-flex align-items-start mb-8'>
            <i className='bi bi-info-circle-fill fs-4 me-3 mt-1'></i>
            <div>
              O valor deixado de gerar é uma estimativa da taxa base ({stats.baseCashbackRate}%)
              sobre as vendas do período, respeitando pedido mínimo e teto por pedido.{' '}
              {getRulesHint(isFood)}
            </div>
          </div>

          <div className='d-flex align-items-center justify-content-between mb-4'>
            <h4 className='fw-bolder text-dark mb-0'>Eficácia do programa</h4>
          </div>
          <div className='row g-5 mb-8'>
            <div className='col-xl-3 col-md-6'>
              <div className='card bg-body shadow-sm border-0 h-100'>
                <div className='card-body'>
                  <div className='d-flex align-items-center justify-content-between mb-3'>
                    <span className='text-gray-600 fw-bold fs-6'>Ticket com resgate</span>
                    <MetricInfo content={ticketCompareInfo} />
                  </div>
                  <span className='text-dark fw-bolder fs-2'>
                    {stats.ordersWithRedeem > 0 ? formatMoney(stats.ticketWithRedeem) : 'N/A'}
                  </span>
                  <span className='text-muted fw-semibold fs-7 d-block mt-1'>
                    Sem resgate{' '}
                    {stats.ordersWithoutRedeem > 0
                      ? formatMoney(stats.ticketWithoutRedeem)
                      : 'N/A'}
                  </span>
                  <span className='text-muted fs-8 d-block mt-1'>
                    {stats.ordersWithRedeem} com uso · {stats.ordersWithoutRedeem} sem uso
                    {stats.ticketDeltaPercent !== null
                      ? ` · ${stats.ticketDeltaPercent > 0 ? '+' : ''}${formatPercent(
                          stats.ticketDeltaPercent
                        )}`
                      : ''}
                  </span>
                </div>
              </div>
            </div>
            <div className='col-xl-3 col-md-6'>
              <div className='card bg-body shadow-sm border-0 h-100'>
                <div className='card-body'>
                  <div className='d-flex align-items-center justify-content-between mb-3'>
                    <span className='text-gray-600 fw-bold fs-6'>Participação no checkout</span>
                    <MetricInfo content={checkoutShareInfo} />
                  </div>
                  <span className='text-dark fw-bolder fs-2'>{formatPercent(stats.checkoutShare)}</span>
                  <span className='text-muted fw-semibold fs-7 d-block mt-1'>
                    {formatMoney(stats.redeemed)} de {formatMoney(stats.sales)}
                  </span>
                </div>
              </div>
            </div>
            <div className='col-xl-3 col-md-6'>
              <div className='card bg-body shadow-sm border-0 h-100'>
                <div className='card-body'>
                  <div className='d-flex align-items-center justify-content-between mb-3'>
                    <span className='text-gray-600 fw-bold fs-6'>Vendas por real resgatado</span>
                    <MetricInfo content={salesPerRedeemedInfo} />
                  </div>
                  <span className='text-dark fw-bolder fs-2'>
                    {stats.salesPerRedeemedReal !== null
                      ? formatMoney(stats.salesPerRedeemedReal)
                      : 'N/A'}
                  </span>
                  <span className='text-muted fw-semibold fs-7 d-block mt-1'>
                    {formatMoney(stats.salesWithRedeem)} em pedidos com resgate
                  </span>
                </div>
              </div>
            </div>
            <div className='col-xl-3 col-md-6'>
              <div className='card bg-body shadow-sm border-0 h-100'>
                <div className='card-body'>
                  <div className='d-flex align-items-center justify-content-between mb-3'>
                    <span className='text-gray-600 fw-bold fs-6'>Tempo médio de resgate</span>
                    <MetricInfo content={avgRedeemTimeInfo} />
                  </div>
                  <span className='text-dark fw-bolder fs-2'>{formatDays(stats.avgRedeemDays)}</span>
                  <span className='text-muted fw-semibold fs-7 d-block mt-1'>
                    {stats.redeemPairsCount > 0
                      ? `${stats.redeemPairsCount} par${
                          stats.redeemPairsCount === 1 ? '' : 'es'
                        } no período`
                      : 'Sem acúmulo anterior no período'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className='card border border-gray-300 shadow-none mb-8'>
            <div className='card-header pt-5 border-0 align-items-center d-flex justify-content-between'>
              <h3 className='card-title flex-column m-0'>
                <span className='card-label fw-bolder text-dark'>Evolução no período</span>
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {chartView === 'volume'
                    ? 'Vendas, cashback gerado e resgates'
                    : 'Primeiro resgate no período vs quem já tinha resgatado'}
                </span>
              </h3>
              <div className='card-toolbar d-flex align-items-center gap-3 flex-wrap'>
                <MetricInfo content={trendReportInfo} />
                <div className='btn-group' role='group'>
                  <button
                    type='button'
                    className={`btn btn-sm ${
                      chartView === 'volume' ? 'btn-primary' : 'btn-light-primary'
                    }`}
                    onClick={() => setChartView('volume')}
                  >
                    Valores
                  </button>
                  <button
                    type='button'
                    className={`btn btn-sm ${
                      chartView === 'redeemers' ? 'btn-primary' : 'btn-light-primary'
                    }`}
                    onClick={() => setChartView('redeemers')}
                  >
                    Clientes
                  </button>
                </div>
                <div className='btn-group' role='group'>
                  <button
                    type='button'
                    className={`btn btn-sm ${
                      timeGrouping === 'day' ? 'btn-primary' : 'btn-light-primary'
                    }`}
                    onClick={() => setTimeGrouping('day')}
                  >
                    Dia
                  </button>
                  <button
                    type='button'
                    className={`btn btn-sm ${
                      timeGrouping === 'month' ? 'btn-primary' : 'btn-light-primary'
                    }`}
                    onClick={() => setTimeGrouping('month')}
                  >
                    Mês
                  </button>
                  <button
                    type='button'
                    className={`btn btn-sm ${
                      timeGrouping === 'year' ? 'btn-primary' : 'btn-light-primary'
                    }`}
                    onClick={() => setTimeGrouping('year')}
                  >
                    Ano
                  </button>
                </div>
              </div>
            </div>
            <div className='card-body pt-5'>
              {trendData.length > 0 ? (
                <Chart
                  key={chartView}
                  options={
                    chartView === 'volume'
                      ? {
                          chart: {
                            id: 'cashback-trend',
                            toolbar: {show: false},
                            zoom: {enabled: false},
                          },
                          stroke: {width: [0, 3, 3], curve: 'smooth'},
                          colors: ['#009EF7', '#50CD89', '#FFC700'],
                          xaxis: {
                            categories: trendData.map((item) =>
                              formatTrendLabel(item.date, timeGrouping)
                            ),
                            labels: {style: {colors: '#A1A5B7', fontSize: '12px'}},
                          },
                          yaxis: [
                            {
                              seriesName: 'Vendas',
                              title: {
                                text: 'Vendas (R$)',
                                style: {color: '#009EF7', fontWeight: 600},
                              },
                              labels: {formatter: (val) => formatMoney(val)},
                            },
                            {
                              seriesName: 'Cashback gerado',
                              opposite: true,
                              title: {
                                text: 'Cashback (R$)',
                                style: {color: '#50CD89', fontWeight: 600},
                              },
                              labels: {formatter: (val) => formatMoney(val)},
                            },
                          ],
                          plotOptions: {bar: {borderRadius: 4, columnWidth: '30%'}},
                          dataLabels: {enabled: false},
                          legend: {position: 'top', horizontalAlign: 'center'},
                          grid: {borderColor: '#E4E6EF', strokeDashArray: 4},
                          tooltip: {
                            shared: true,
                            y: {formatter: (value) => formatMoney(Number(value || 0))},
                          },
                        }
                      : {
                          chart: {
                            id: 'cashback-redeemers',
                            toolbar: {show: false},
                            zoom: {enabled: false},
                            stacked: false,
                          },
                          stroke: {width: 0},
                          colors: ['#50CD89', '#7239EA'],
                          xaxis: {
                            categories: trendData.map((item) =>
                              formatTrendLabel(item.date, timeGrouping)
                            ),
                            labels: {style: {colors: '#A1A5B7', fontSize: '12px'}},
                          },
                          yaxis: {
                            title: {
                              text: 'Clientes',
                              style: {color: '#3F4254', fontWeight: 600},
                            },
                            labels: {formatter: (val) => String(Math.round(Number(val || 0)))},
                            min: 0,
                            forceNiceScale: true,
                          },
                          plotOptions: {bar: {borderRadius: 4, columnWidth: '45%'}},
                          dataLabels: {enabled: false},
                          legend: {position: 'top', horizontalAlign: 'center'},
                          grid: {borderColor: '#E4E6EF', strokeDashArray: 4},
                          tooltip: {
                            shared: true,
                            intersect: false,
                            y: {
                              formatter: (value) =>
                                `${Math.round(Number(value || 0))} cliente${
                                  Number(value || 0) === 1 ? '' : 's'
                                }`,
                            },
                          },
                        }
                  }
                  series={
                    chartView === 'volume'
                      ? [
                          {
                            name: 'Vendas',
                            type: 'column',
                            data: trendData.map((item) => item.sales),
                          },
                          {
                            name: 'Cashback gerado',
                            type: 'line',
                            data: trendData.map((item) => item.earned),
                          },
                          {
                            name: 'Cashback resgatado',
                            type: 'line',
                            data: trendData.map((item) => item.redeemed),
                          },
                        ]
                      : [
                          {
                            name: 'Primeiro resgate no período',
                            type: 'column',
                            data: trendData.map((item) => item.firstRedeemers),
                          },
                          {
                            name: 'Já tinha resgatado no período',
                            type: 'column',
                            data: trendData.map((item) => item.repeatRedeemers),
                          },
                        ]
                  }
                  type={chartView === 'volume' ? 'line' : 'bar'}
                  height={350}
                />
              ) : (
                <div className='text-muted text-center py-10'>
                  Não há movimentações no período selecionado.
                </div>
              )}
            </div>
          </div>

          <div className='card border border-gray-300 shadow-none'>
            <div className='card-header pt-5 border-0 d-flex align-items-center justify-content-between'>
              <h3 className='card-title flex-column m-0'>
                <span className='card-label fw-bolder text-dark'>Clientes com mais cashback</span>
                <span className='text-muted mt-1 fw-bold fs-7'>
                  Top 20 clientes por acúmulo no período
                </span>
              </h3>
              <MetricInfo content={topCustomersInfo} />
            </div>
            <div className='card-body'>
              <Datatable
                data={customerRows}
                headers={[
                  {key: 'Cliente', value: 'customerName', type: 'default'},
                  {key: 'E-mail', value: 'email', type: 'default'},
                  {key: 'Pedidos', value: 'orders', type: 'default'},
                  {key: 'Gerado', value: 'earnedDisplay', type: 'default'},
                  {key: 'Resgatado', value: 'redeemedDisplay', type: 'default'},
                ]}
                color='primary'
                search
                showControls
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const CashbackReport: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: false},
          {title: 'Cashback', path: 'cashback', isActive: false},
          {title: 'Relatório', path: 'cashback/report', isActive: true},
        ]}
      >
        Relatório de Cashback
      </PageTitle>
      <CashbackReportContent />
    </>
  )
}

export {CashbackReport}
